import { ChangeEvent, useState, useEffect } from "react";
import SubmitSkeleton from "../SubmitSkeleton";
import { useCaseContext } from "../../../context/CaseContext";
import MediaDropzone from "../../common/MediaDropZone";
import Select from "react-select";
import {
  abuserAuthorityPositionOptions,
  abuserVictimRelationshipOptions,
  ethnicityOptions,
  genderOptions,
  occupationOptions,
  referenceAge,
} from "../resources";
import CustomLabel from "../../common/CustomLabel";
import { AbuserDetails as AbuserDetailsType } from "../../../types";
import SocialMediaSelector from "../SocialMediaSelector";

const AbuserDetails = ({ setStep }: { setStep: (step: number) => void }) => {
  const { abuserDetails, updateCaseData } = useCaseContext();
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize hasSpecialAuthority if undefined
  useEffect(() => {
    if (abuserDetails.hasSpecialAuthority === undefined) {
      updateCaseData("abuserDetails", {
        ...abuserDetails,
        hasSpecialAuthority: false,
      });
    }
  }, [abuserDetails, updateCaseData]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Required fields validation
    if (!abuserDetails.gender) {
      newErrors.gender = "Please select the abuser's gender";
    }

    if (!abuserDetails.refAge) {
      newErrors.refAge = "Please select the abuser's reference age";
    }

    if (!abuserDetails.ethnicity) {
      newErrors.ethnicity = "Please select the abuser's ethnicity";
    } else if (
      typeof abuserDetails.ethnicity === "object" &&
      abuserDetails.ethnicity.custom &&
      !abuserDetails.ethnicity.customValue
    ) {
      newErrors.ethnicityCustom = "Please specify the ethnicity";
    }

    if (abuserDetails.hasSpecialAuthority === undefined) {
      newErrors.hasSpecialAuthority =
        "Please indicate if the abuser has special authority";
    }

    if (abuserDetails.hasSpecialAuthority && !abuserDetails.authorityPosition) {
      newErrors.authorityPosition =
        "Please select the abuser's authority position";
    } else if (
      abuserDetails.hasSpecialAuthority &&
      typeof abuserDetails.authorityPosition === "object" &&
      abuserDetails.authorityPosition?.custom &&
      !abuserDetails.authorityPosition.customValue
    ) {
      newErrors.authorityPositionCustom =
        "Please specify the authority position";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAbuserDetailChange = (
    val: string | number | File[] | boolean | null | undefined,
    field: keyof AbuserDetailsType
  ) => {
    // Clear relevant error when field is updated
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    const otherFields = [
      "ethnicity",
      "relation",
      "occupation",
      "authorityPosition",
    ];

    if (otherFields.includes(field) && val === "other") {
      const otherVal = { custom: true, customValue: "" };

      updateCaseData("abuserDetails", {
        ...abuserDetails,
        [field]: otherVal,
      });
    } else {
      updateCaseData("abuserDetails", {
        ...abuserDetails,
        [field]: val,
      });
    }
  };

  const handleOtherChange = (
    field: "ethnicity" | "relation" | "occupation" | "authorityPosition",
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.target;

    // Clear custom field error when value is entered
    if (errors[`${field}Custom`] && value) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`${field}Custom`];
        return newErrors;
      });
    }

    updateCaseData("abuserDetails", {
      ...abuserDetails,
      [field]: {
        custom: true,
        customValue: value,
      },
    });
  };

  // Handle photo description files
  const handlePhotoDescriptionChange = (files: File[]) => {
    handleAbuserDetailChange(
      files.length > 0 ? files : null,
      "photoDescription"
    );
  };

  const handleNextStep = () => {
    const isValid = validateForm();
    if (isValid) {
      setStep(9);
    }
  };

  const canProceed =
    abuserDetails.gender &&
    abuserDetails.refAge &&
    abuserDetails.ethnicity &&
    (typeof abuserDetails.ethnicity !== "object" ||
      (typeof abuserDetails.ethnicity === "object" &&
        abuserDetails.ethnicity.customValue)) &&
    abuserDetails.hasSpecialAuthority !== undefined &&
    (!abuserDetails.hasSpecialAuthority ||
      (abuserDetails.hasSpecialAuthority &&
        abuserDetails.authorityPosition &&
        (typeof abuserDetails.authorityPosition !== "object" ||
          (typeof abuserDetails.authorityPosition === "object" &&
            abuserDetails.authorityPosition.customValue))));

  return (
    <SubmitSkeleton
      title=""
      onPrev={() => setStep(7)}
      onNext={handleNextStep}
      canProceed={!!canProceed}
      progress={8 / 10}
    >
      <div className="flex flex-col gap-y-5">
        <CustomLabel labelText="Name of the abuser (Optional)">
          <input
            type="text"
            placeholder="Abuser's name"
            value={abuserDetails?.name || ""}
            onChange={(e) => handleAbuserDetailChange(e.target.value, "name")}
            className="font-medium text-[14px] leading-4 text-gray-7 w-full border border-gray-3 rounded-md py-2 px-3 outline-none focus:ring-1 focus:ring-black focus:border-transparent cursor-pointer"
          />
        </CustomLabel>

        <CustomLabel labelText="Abuser's Alias or Nickname (Optional)">
          <input
            type="text"
            placeholder="Abuser's alias or nickname"
            value={abuserDetails?.alias || ""}
            onChange={(e) => handleAbuserDetailChange(e.target.value, "alias")}
            className="font-medium text-[14px] leading-4 text-gray-7 w-full border border-gray-3 rounded-md py-2 px-3 outline-none focus:ring-1 focus:ring-black focus:border-transparent cursor-pointer"
          />
        </CustomLabel>

        <CustomLabel labelText="Abuser's Gender" isRequired>
          <Select
            components={{
              IndicatorSeparator: () => null,
            }}
            className="font-medium text-[14px] leading-4 cursor-pointer"
            placeholder="Select gender"
            defaultInputValue={abuserDetails?.gender || ""}
            onChange={(selectedOption) =>
              handleAbuserDetailChange(selectedOption?.value, "gender")
            }
            options={genderOptions}
          />
          {errors.gender && (
            <p className="text-red-500 text-sm mt-1">{errors.gender}</p>
          )}
        </CustomLabel>

        <CustomLabel labelText="Abuser's Reference age" isRequired>
          <Select
            components={{
              IndicatorSeparator: () => null,
            }}
            className="font-medium text-[14px] leading-4 cursor-pointer"
            placeholder="Select reference age range"
            defaultInputValue={abuserDetails?.refAge?.toString() || ""}
            onChange={(selectedOption) =>
              handleAbuserDetailChange(selectedOption?.value, "refAge")
            }
            options={referenceAge}
          />
          {errors.refAge && (
            <p className="text-red-500 text-sm mt-1">{errors.refAge}</p>
          )}
        </CustomLabel>

        <CustomLabel labelText="Ethnicity" isRequired>
          <Select
            components={{
              IndicatorSeparator: () => null,
            }}
            className="font-medium text-[14px] leading-4 cursor-pointer"
            placeholder="Select ethnicity"
            defaultInputValue={
              typeof abuserDetails.ethnicity === "object"
                ? "other"
                : abuserDetails?.ethnicity || ""
            }
            onChange={(selectedOption) =>
              handleAbuserDetailChange(selectedOption?.value, "ethnicity")
            }
            options={ethnicityOptions}
          />
          {errors.ethnicity && (
            <p className="text-red-500 text-sm mt-1">{errors.ethnicity}</p>
          )}
        </CustomLabel>

        {abuserDetails?.ethnicity &&
          typeof abuserDetails.ethnicity === "object" &&
          abuserDetails.ethnicity.custom && (
            <label className="flex flex-col gap-y-2">
              <span className="font-bold text-[14px] leading-[16.94px] text-black mt-2">
                Other
              </span>
              <input
                type="text"
                value={abuserDetails.ethnicity.customValue}
                onChange={(e) => handleOtherChange("ethnicity", e)}
                placeholder="Please specify..."
                className="border border-gray-3 py-2 px-3 rounded-md box-border outline-none focus:ring-1 focus:ring-black focus:border-transparent"
              />
              {errors.ethnicityCustom && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.ethnicityCustom}
                </p>
              )}
            </label>
          )}

        <CustomLabel labelText="Abuser's Relation to Victim (Optional)">
          <Select
            components={{
              IndicatorSeparator: () => null,
            }}
            className="font-medium text-[14px] leading-4 cursor-pointer"
            placeholder="Select abuser's relation to victim"
            defaultInputValue={
              typeof abuserDetails.relation === "object"
                ? "other"
                : abuserDetails?.relation || ""
            }
            onChange={(selectedOption) =>
              handleAbuserDetailChange(selectedOption?.value, "relation")
            }
            options={abuserVictimRelationshipOptions}
          />
        </CustomLabel>

        {abuserDetails?.relation &&
          typeof abuserDetails.relation === "object" &&
          abuserDetails.relation.custom && (
            <label className="flex flex-col gap-y-2">
              <span className="font-bold text-[14px] leading-[16.94px] text-black mt-2">
                Other
              </span>
              <input
                type="text"
                value={abuserDetails.relation.customValue}
                onChange={(e) => handleOtherChange("relation", e)}
                placeholder="Please specify..."
                className="border border-gray-3 py-2 px-3 rounded-md box-border outline-none focus:ring-1 focus:ring-black focus:border-transparent"
              />
            </label>
          )}

        <CustomLabel labelText="Abuser's Occupation (Optional)">
          <Select
            components={{
              IndicatorSeparator: () => null,
            }}
            className="font-medium text-[14px] leading-4 cursor-pointer"
            placeholder="Select abuser's occupation"
            defaultInputValue={
              typeof abuserDetails.occupation === "object"
                ? "other"
                : abuserDetails?.occupation || ""
            }
            onChange={(selectedOption) =>
              handleAbuserDetailChange(selectedOption?.value, "occupation")
            }
            options={occupationOptions}
          />
        </CustomLabel>

        {abuserDetails?.occupation &&
          typeof abuserDetails.occupation === "object" &&
          abuserDetails.occupation.custom && (
            <label className="flex flex-col gap-y-2">
              <span className="font-bold text-[14px] leading-[16.94px] text-black mt-2">
                Other
              </span>
              <input
                type="text"
                value={abuserDetails.occupation.customValue}
                onChange={(e) => handleOtherChange("occupation", e)}
                placeholder="Please specify..."
                className="border border-gray-3 py-2 px-3 rounded-md box-border outline-none focus:ring-1 focus:ring-black focus:border-transparent"
              />
            </label>
          )}

        <SocialMediaSelector
          labelText="Abuser's Social Media (Optional)"
          value={abuserDetails.socials}
          setValue={(updatedSocials) =>
            updateCaseData("abuserDetails", {
              ...abuserDetails,
              socials: updatedSocials,
            })
          }
        />

        <div className="flex flex-col gap-y-2">
          <CustomLabel
            labelText="Does this person have special authority"
            isRequired
          />

          <div className="flex flex-col gap-y-6">
            <label className="flex items-center gap-2 pr-6 pl-3 cursor-pointer">
              <input
                type="radio"
                name="hasSpecialAuthority"
                value="yes"
                checked={abuserDetails?.hasSpecialAuthority === true}
                onChange={() =>
                  handleAbuserDetailChange(true, "hasSpecialAuthority")
                }
                className="form-radio"
              />
              <span className="font-[400] text-[14px] leading-[16.94px] text-black">
                Yes
              </span>
            </label>

            <label className="flex items-center gap-2 pr-6 pl-3 cursor-pointer">
              <input
                type="radio"
                name="hasSpecialAuthority"
                value="no"
                checked={abuserDetails?.hasSpecialAuthority === false}
                onChange={() =>
                  handleAbuserDetailChange(false, "hasSpecialAuthority")
                }
                className="form-radio"
              />
              <span className="font-[400] text-[14px] leading-[16.94px] text-black">
                No
              </span>
            </label>
          </div>
          {errors.hasSpecialAuthority && (
            <p className="text-red-500 text-sm mt-1">
              {errors.hasSpecialAuthority}
            </p>
          )}
        </div>

        {abuserDetails?.hasSpecialAuthority && (
          <>
            <CustomLabel labelText="Abuser's Authority Position" isRequired>
              <Select
                components={{
                  IndicatorSeparator: () => null,
                }}
                className="font-medium text-[14px] leading-4 cursor-pointer"
                placeholder="Select abuser's authority position"
                defaultInputValue={
                  typeof abuserDetails.authorityPosition === "object"
                    ? "other"
                    : abuserDetails?.authorityPosition || ""
                }
                onChange={(selectedOption) =>
                  handleAbuserDetailChange(
                    selectedOption?.value,
                    "authorityPosition"
                  )
                }
                options={abuserAuthorityPositionOptions}
              />
              {errors.authorityPosition && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.authorityPosition}
                </p>
              )}
            </CustomLabel>

            {abuserDetails?.authorityPosition &&
              typeof abuserDetails.authorityPosition === "object" &&
              abuserDetails.authorityPosition.custom && (
                <label className="flex flex-col gap-y-2">
                  <span className="font-bold text-[14px] leading-[16.94px] text-black mt-2">
                    Other
                  </span>
                  <input
                    type="text"
                    value={abuserDetails.authorityPosition.customValue}
                    onChange={(e) => handleOtherChange("authorityPosition", e)}
                    placeholder="Please specify..."
                    className="border border-gray-3 py-2 px-3 rounded-md box-border outline-none focus:ring-1 focus:ring-black focus:border-transparent"
                  />
                  {errors.authorityPositionCustom && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.authorityPositionCustom}
                    </p>
                  )}
                </label>
              )}
          </>
        )}

        <CustomLabel labelText="Abuser's Physical Description (Optional)">
          <textarea
            placeholder="Describe the abuser"
            className="h-[232px] w-full border border-gray-3 rounded-[10px] py-[15.5px] px-[18px] font-medium text-gray-7 text-[14px] leading-4 outline-none focus:ring-1 focus:ring-black focus:border-transparent cursor-pointer"
            value={abuserDetails?.phyDescription || ""}
            onChange={(e) =>
              handleAbuserDetailChange(e.target.value, "phyDescription")
            }
          />
        </CustomLabel>

        <CustomLabel labelText="Abuser's Photo Description (Optional)">
          <MediaDropzone
            contextField="photoDescription"
            onFileChange={handlePhotoDescriptionChange}
            existingFiles={abuserDetails.photoDescription ?? undefined}
            acceptedTypes={["image/*"]}
            label="Drop photos of the abuser here"
          />
        </CustomLabel>
      </div>
    </SubmitSkeleton>
  );
};

export default AbuserDetails;
